import { FormEvent, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from '@/components/ui/toast';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  PasswordMatchHint,
  PasswordRequirementList,
} from '@/components/auth/PasswordRequirementList';
import { useAuth } from '@/hooks/useAuth';
import { useAccountSessions } from '@/hooks/useAccountSessions';
import { downloadAccountDataExport, useAccountDeletion } from '@/hooks/useAccountDeletion';
import { describeAccountSession } from '@/lib/accountSessions';
import { getPasswordIssueMessages } from '@/lib/passwordStrength';
import { useRole } from '@/hooks/useRole';
import { useOrg } from '@/hooks/useOrg';
import { useMfa } from '@/hooks/useMfa';
import { canConfigureMfa } from '@/lib/mfaPolicy';
import { useAuditLog } from '@/hooks/useAuditLog';
import { SECURITY_EVENT_LABELS, isSecurityEventAction } from '@/lib/securityEvents';
import { formatDateTime } from '@/lib/utils';
import { getActionErrorMessage } from '@/lib/errorMessages';
import { WorklynxIcon } from '@/components/icons/WorklynxIcon';
import type { WorklynxIconName } from '@/components/icons/iconData';

interface AccountSecurityDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  fullName?: string | null;
  email?: string | null;
}

type SignOutScope = 'local' | 'others' | 'global';
type AccountSection = 'overview' | 'password' | 'data';

export function AccountSecurityDialog({
  open,
  onOpenChange,
  fullName,
  email,
}: AccountSecurityDialogProps) {
  const navigate = useNavigate();
  const { user, updatePassword, signOut, signOutOtherDevices, signOutEverywhere } = useAuth();
  const { role, isAdmin } = useRole();
  const { organization } = useOrg();
  const isMfaEligible = canConfigureMfa(role);
  const mfa = useMfa({ enabled: open && isMfaEligible });
  const sessions = useAccountSessions(open);
  const securityHistory = useAuditLog({ userId: user?.id, limit: 20 });
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const canReviewAccountDeletion = isAdmin || role === 'BO';
  const accountDeletion = useAccountDeletion({
    enabled: open,
    userId: user?.id,
    canReview: canReviewAccountDeletion,
  });
  const [showDeletionRequest, setShowDeletionRequest] = useState(false);
  const [deletionReason, setDeletionReason] = useState('');
  const [deletionConfirmation, setDeletionConfirmation] = useState('');
  const [requestDataExport, setRequestDataExport] = useState(true);
  const [pendingSignOut, setPendingSignOut] = useState<SignOutScope | null>(null);
  const [activeSection, setActiveSection] = useState<AccountSection>('overview');

  const passwordContext = useMemo(() => ({ email, fullName }), [email, fullName]);
  const sessionList = sessions.data ?? [];
  const otherSessionCount = sessionList.filter((session) => !session.is_current).length;

  const resetPasswordFields = () => {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setMfaCode('');
    setPasswordError(null);
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      resetPasswordFields();
      setShowDeletionRequest(false);
      setDeletionReason('');
      setDeletionConfirmation('');
      setRequestDataExport(true);
      setPendingSignOut(null);
      setActiveSection('overview');
    }

    onOpenChange(nextOpen);
  };

  const handlePasswordSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setPasswordError(null);

    if (!currentPassword) {
      setPasswordError('Enter your current password before changing it.');
      return;
    }

    if (newPassword === currentPassword) {
      setPasswordError('Choose a new password that is different from your current password.');
      return;
    }

    const passwordIssues = getPasswordIssueMessages(newPassword, passwordContext);
    if (passwordIssues.length > 0) {
      setPasswordError(`Password requirement: ${passwordIssues[0]}.`);
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError('Passwords do not match.');
      return;
    }

    try {
      await updatePassword.mutateAsync({ newPassword, currentPassword, mfaCode });
      resetPasswordFields();
      toast.success('Password updated successfully.');
    } catch (error) {
      const message = getActionErrorMessage(error, 'Unable to update password.');
      setPasswordError(message);
      toast.error(message);
    }
  };

  const handleSignOut = async () => {
    if (!pendingSignOut) return;

    try {
      if (pendingSignOut === 'local') {
        await signOut.mutateAsync();
      } else if (pendingSignOut === 'others') {
        await signOutOtherDevices.mutateAsync();
        await sessions.refetch();
        toast.success('Other signed-in devices have been signed out.');
      } else {
        await signOutEverywhere.mutateAsync();
      }

      const completedScope = pendingSignOut;
      setPendingSignOut(null);
      if (completedScope !== 'others') {
        onOpenChange(false);
        toast.success(
          completedScope === 'local'
            ? 'This device has been signed out.'
            : 'You have been signed out everywhere.',
        );
      }
    } catch (error) {
      const message =
        getActionErrorMessage(error, 'Unable to sign out the selected session.');
      toast.error(message);
    }
  };

  const handleAccountDeletionRequest = async () => {
    if (deletionConfirmation !== 'DELETE') {
      toast.error('Type DELETE to confirm the account deletion request.');
      return;
    }

    try {
      await accountDeletion.requestDeletion.mutateAsync({
        organizationId: organization?.id ?? null,
        reason: deletionReason.trim(),
        requestExport: requestDataExport,
      });
      setShowDeletionRequest(false);
      setDeletionReason('');
      setDeletionConfirmation('');
      toast.success('Account deletion request submitted for review.');
    } catch (error) {
      toast.error(
        getActionErrorMessage(error, 'Unable to submit account deletion request.'),
      );
    }
  };

  const handleAccountExport = async () => {
    try {
      const data = await accountDeletion.exportData.mutateAsync(organization?.id ?? null);
      downloadAccountDataExport(data);
      toast.success('Your account data export has downloaded.');
    } catch (error) {
      toast.error(
        getActionErrorMessage(error, 'Unable to generate account data export.'),
      );
    }
  };

  const handleDeletionReview = async (requestId: string, decision: 'APPROVE' | 'REJECT') => {
    try {
      await accountDeletion.reviewDeletion.mutateAsync({ requestId, decision });
      toast.success(
        decision === 'APPROVE' ? 'Deletion request approved.' : 'Deletion request declined.',
      );
    } catch (error) {
      toast.error(getActionErrorMessage(error, 'Unable to review deletion request.'));
    }
  };

  const securityEvents = (securityHistory.data ?? [])
    .filter((event) => isSecurityEventAction(event.action))
    .slice(0, 5);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-5xl overflow-hidden">
        <DialogHeader className="border-b-0 bg-gradient-to-r from-brand-soft via-card to-card pb-4">
          <div className="flex items-start gap-3">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-brand text-white shadow-sm">
              <WorklynxIcon name="roles" size={20} />
            </span>
            <div>
              <DialogTitle>Account security</DialogTitle>
              <DialogDescription>
                Manage devices, sign-in protection, and your account data.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <nav
          className="border-brand-border/20 bg-bg/40 flex gap-1 overflow-x-auto border-y px-5 py-2"
          aria-label="Account settings"
        >
          <AccountTab
            active={activeSection === 'overview'}
            onClick={() => setActiveSection('overview')}
            icon="roles"
          >
            Overview
          </AccountTab>
          <AccountTab
            active={activeSection === 'password'}
            onClick={() => setActiveSection('password')}
            icon="key"
          >
            Password
          </AccountTab>
          <AccountTab
            active={activeSection === 'data'}
            onClick={() => setActiveSection('data')}
            icon="delete"
          >
            Data & deletion
          </AccountTab>
        </nav>

        <div className="max-h-[calc(92vh-13rem)] overflow-y-auto p-5">
          {activeSection === 'overview' && (
            <div className="space-y-5">
              <div className="grid gap-4 lg:grid-cols-[1.35fr_0.65fr]">
                <section className="border-brand-border/35 bg-bg/35 rounded-lg border p-4">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="flex items-start gap-3">
                      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-brand-soft text-brand">
                        <WorklynxIcon name="devices" size={20} />
                      </span>
                      <div>
                        <h3 className="font-semibold text-foreground">Active sessions</h3>
                        <p className="text-sm text-muted">
                          {sessionList.length} signed-in device{sessionList.length === 1 ? '' : 's'}
                          .
                        </p>
                      </div>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => void sessions.refetch()}
                      disabled={sessions.isFetching}
                    >
                      {sessions.isFetching ? 'Refreshing...' : 'Refresh'}
                    </Button>
                  </div>
                  <div className="mt-4 space-y-2">
                    {sessions.isLoading ? (
                      <p className="text-sm text-muted">Loading active sessions...</p>
                    ) : sessions.isError ? (
                      <p className="rounded-md bg-warning-soft px-3 py-2 text-sm text-warning">
                        Sessions could not be loaded. Refresh and try again.
                      </p>
                    ) : (
                      sessionList.map((session) => {
                        const device = describeAccountSession(session.user_agent);
                        return (
                          <div
                            key={session.session_id}
                            className="border-brand-border/25 flex items-center justify-between gap-3 rounded-md border bg-card px-3 py-2.5"
                          >
                            <div>
                              <p className="font-medium text-foreground">{device.label}</p>
                              <p className="mt-0.5 text-xs text-muted">
                                Last active {formatSessionDate(session.last_active_at)}
                              </p>
                            </div>
                            {session.is_current && (
                              <span className="rounded-full bg-success-soft px-2 py-1 text-xs font-semibold text-success">
                                This device
                              </span>
                            )}
                          </div>
                        );
                      })
                    )}
                    {!sessions.isLoading && !sessions.isError && sessionList.length === 0 && (
                      <p className="text-sm text-muted">No active sessions found.</p>
                    )}
                  </div>
                  {pendingSignOut ? (
                    <div className="mt-4 rounded-md border border-warning/30 bg-warning-soft p-3">
                      <p className="font-medium text-foreground">
                        {getSignOutConfirmationTitle(pendingSignOut)}
                      </p>
                      <p className="mt-1 text-sm text-muted">
                        {getSignOutConfirmationDescription(pendingSignOut)}
                      </p>
                      <div className="mt-3 flex gap-2">
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => setPendingSignOut(null)}
                        >
                          Cancel
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="destructive"
                          onClick={() => void handleSignOut()}
                          disabled={
                            signOut.isPending ||
                            signOutOtherDevices.isPending ||
                            signOutEverywhere.isPending
                          }
                        >
                          Confirm sign out
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="mt-4 flex flex-wrap gap-2">
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => setPendingSignOut('local')}
                      >
                        Sign out this device
                      </Button>
                      {otherSessionCount > 0 && (
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => setPendingSignOut('others')}
                        >
                          Sign out others ({otherSessionCount})
                        </Button>
                      )}
                      <Button
                        type="button"
                        size="sm"
                        variant="destructive"
                        onClick={() => setPendingSignOut('global')}
                      >
                        Sign out everywhere
                      </Button>
                    </div>
                  )}
                </section>

                {isMfaEligible && (
                  <section className="border-brand-border/35 bg-bg/35 rounded-lg border p-4">
                    <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-brand-soft text-brand">
                      <WorklynxIcon name="mobile" size={20} />
                    </span>
                    <div className="mt-4 flex items-center gap-2">
                      <h3 className="font-semibold text-foreground">Authenticator app</h3>
                      <span
                        className={`rounded-full px-2 py-1 text-xs font-semibold ${mfa.hasVerifiedFactor ? 'bg-success-soft text-success' : 'bg-warning-soft text-warning'}`}
                      >
                        {mfa.hasVerifiedFactor ? 'Enabled' : 'Recommended'}
                      </span>
                    </div>
                    <p className="mt-2 text-sm text-muted">
                      {mfa.hasVerifiedFactor
                        ? `${mfa.verifiedFactors.length} authenticator app${mfa.verifiedFactors.length === 1 ? '' : 's'} protects this account.`
                        : 'Add an authenticator app for stronger sign-in protection.'}
                    </p>
                    <Button
                      type="button"
                      className="mt-5 w-full"
                      variant={mfa.hasVerifiedFactor ? 'outline' : 'default'}
                      onClick={() => {
                        handleOpenChange(false);
                        navigate('/setup-mfa?returnTo=%2Fprofile');
                      }}
                      disabled={mfa.isLoadingFactors}
                    >
                      {mfa.hasVerifiedFactor ? 'Manage MFA' : 'Set up MFA'}
                    </Button>
                  </section>
                )}
              </div>

              <section className="border-brand-border/35 bg-bg/35 rounded-lg border">
                <div className="border-brand-border/20 flex items-center justify-between border-b px-4 py-3">
                  <div className="flex items-center gap-2">
                    <WorklynxIcon name="history" size={16} className="text-brand" />
                    <h3 className="font-semibold text-foreground">Security history</h3>
                  </div>
                  <span className="text-xs text-muted">Recent account activity</span>
                </div>
                <div className="divide-brand-border/20 divide-y">
                  {securityHistory.isLoading ? (
                    <p className="px-4 py-4 text-sm text-muted">Loading security history...</p>
                  ) : securityHistory.isError ? (
                    <p className="px-4 py-4 text-sm text-muted">
                      Security history could not be loaded.
                    </p>
                  ) : securityEvents.length === 0 ? (
                    <p className="px-4 py-4 text-sm text-muted">
                      No recent security events for this account.
                    </p>
                  ) : (
                    securityEvents.map((event) => (
                      <div
                        key={event.id}
                        className="flex flex-wrap items-center justify-between gap-2 px-4 py-3"
                      >
                        <span className="text-sm font-medium text-foreground">
                          {isSecurityEventAction(event.action)
                            ? SECURITY_EVENT_LABELS[event.action]
                            : event.action}
                        </span>
                        <span className="text-xs text-muted">
                          {formatDateTime(event.created_at)}
                        </span>
                      </div>
                    ))
                  )}
                </div>
              </section>
            </div>
          )}

          {activeSection === 'password' && (
            <form
              onSubmit={handlePasswordSubmit}
              className="border-brand-border/35 bg-bg/35 mx-auto max-w-2xl rounded-lg border p-5"
            >
              <div className="mb-5 flex items-start gap-3">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-brand-soft text-brand">
                  <WorklynxIcon name="key" size={20} />
                </span>
                <div>
                  <h3 className="font-semibold text-foreground">Change password</h3>
                  <p className="text-sm text-muted">
                    Confirm your current password, then choose a unique new one.
                  </p>
                </div>
              </div>
              <div className="space-y-4">
                <Input
                  label="Current password"
                  name="current-password"
                  type="password"
                  autoComplete="current-password"
                  value={currentPassword}
                  onChange={(event) => setCurrentPassword(event.target.value)}
                />
                <div className="grid gap-4 sm:grid-cols-2">
                  <Input
                    label="New password"
                    name="new-password"
                    type="password"
                    autoComplete="new-password"
                    value={newPassword}
                    onChange={(event) => setNewPassword(event.target.value)}
                  />
                  <Input
                    label="Confirm new password"
                    name="confirm-new-password"
                    type="password"
                    autoComplete="new-password"
                    value={confirmPassword}
                    onChange={(event) => setConfirmPassword(event.target.value)}
                  />
                </div>
                {mfa.hasVerifiedFactor && (
                  <Input
                    label="Authenticator code"
                    name="password-change-mfa-code"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    value={mfaCode}
                    onChange={(event) => setMfaCode(event.target.value.replace(/\D/g, ''))}
                    description="Required to confirm this password change."
                  />
                )}
                <PasswordRequirementList password={newPassword} context={passwordContext} />
                <PasswordMatchHint password={newPassword} confirmPassword={confirmPassword} />
                {passwordError && (
                  <div className="rounded-md border border-danger/25 bg-danger-soft px-3 py-2 text-sm text-danger">
                    {passwordError}
                  </div>
                )}
                <div className="flex justify-end">
                  <Button
                    type="submit"
                    disabled={
                      updatePassword.isPending || (mfa.hasVerifiedFactor && mfaCode.length !== 6)
                    }
                  >
                    {updatePassword.isPending ? 'Updating...' : 'Update password'}
                  </Button>
                </div>
              </div>
            </form>
          )}

          {activeSection === 'data' && (
            <div className="space-y-5">
              <section className="rounded-lg border border-danger/25 bg-danger-soft/35 p-5">
                <div className="flex items-start gap-3">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-danger/10 text-danger">
                    <WorklynxIcon name="delete" size={20} />
                  </span>
                  <div>
                    <h3 className="font-semibold text-foreground">Account deletion</h3>
                    <p className="mt-1 text-sm text-muted">
                      This is a controlled request. Required payroll, invoice, safety, and audit
                      records remain protected during the retention period.
                    </p>
                  </div>
                </div>
                {accountDeletion.myRequest.isLoading ? (
                  <p className="mt-4 text-sm text-muted">Checking your deletion request...</p>
                ) : accountDeletion.myRequest.data ? (
                  <div className="mt-4 rounded-md border border-warning/30 bg-warning-soft p-3 text-sm">
                    <p className="font-semibold text-foreground">
                      {getDeletionRequestTitle(accountDeletion.myRequest.data.status)}
                    </p>
                    <p className="mt-1 text-muted">
                      {getDeletionRequestDescription(accountDeletion.myRequest.data)}
                    </p>
                    {canCancelDeletion(accountDeletion.myRequest.data.status) && (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="mt-3"
                        onClick={() =>
                          void accountDeletion.cancelDeletion
                            .mutateAsync(accountDeletion.myRequest.data!.id)
                            .then(() => toast.success('Account deletion request cancelled.'))
                            .catch((error: unknown) =>
                              toast.error(
                                getActionErrorMessage(
                                  error,
                                  'Unable to cancel account deletion request.',
                                ),
                              ),
                            )
                        }
                        disabled={accountDeletion.cancelDeletion.isPending}
                      >
                        Cancel deletion request
                      </Button>
                    )}
                  </div>
                ) : showDeletionRequest ? (
                  <div className="mt-4 space-y-3 rounded-md border border-danger/25 bg-card p-4">
                    <label
                      className="block text-sm font-medium text-foreground"
                      htmlFor="account-deletion-reason"
                    >
                      Reason for leaving <span className="font-normal text-muted">(optional)</span>
                    </label>
                    <textarea
                      id="account-deletion-reason"
                      value={deletionReason}
                      maxLength={1000}
                      onChange={(event) => setDeletionReason(event.target.value)}
                      className="min-h-20 w-full rounded-md border border-brand-border bg-bg px-3 py-2 text-sm text-foreground outline-none focus:border-brand"
                      placeholder="Tell us anything the reviewer should know."
                    />
                    <label className="flex items-start gap-2 text-sm text-foreground">
                      <input
                        type="checkbox"
                        checked={requestDataExport}
                        onChange={(event) => setRequestDataExport(event.target.checked)}
                        className="mt-0.5 h-4 w-4 accent-brand"
                      />
                      <span>
                        Download my personal account data before deletion. Sensitive bank and tax
                        values, document files, and other people&apos;s data are excluded.
                      </span>
                    </label>
                    <Input
                      label="Type DELETE to confirm"
                      name="account-deletion-confirmation"
                      value={deletionConfirmation}
                      onChange={(event) => setDeletionConfirmation(event.target.value)}
                      autoComplete="off"
                    />
                    <div className="flex flex-wrap gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setShowDeletionRequest(false)}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="button"
                        variant="destructive"
                        onClick={() => void handleAccountDeletionRequest()}
                        disabled={
                          accountDeletion.requestDeletion.isPending ||
                          deletionConfirmation !== 'DELETE'
                        }
                      >
                        {accountDeletion.requestDeletion.isPending
                          ? 'Submitting...'
                          : 'Submit deletion request'}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="mt-4 flex flex-wrap gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => void handleAccountExport()}
                      disabled={accountDeletion.exportData.isPending}
                    >
                      {accountDeletion.exportData.isPending
                        ? 'Preparing export...'
                        : 'Download my data'}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      className="border-danger/40 text-danger hover:bg-danger-soft"
                      onClick={() => setShowDeletionRequest(true)}
                    >
                      Request account deletion
                    </Button>
                  </div>
                )}
              </section>
              {canReviewAccountDeletion && (
                <section className="border-brand-border/35 bg-bg/35 rounded-lg border p-5">
                  <div className="flex items-start gap-3">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-warning-soft text-warning">
                      <WorklynxIcon name="warning" size={20} />
                    </span>
                    <div>
                      <h3 className="font-semibold text-foreground">Deletion reviews</h3>
                      <p className="text-sm text-muted">
                        Review offboarding and ownership requirements before approving.
                      </p>
                    </div>
                  </div>
                  <div className="mt-4 space-y-2">
                    {accountDeletion.reviewQueue.isLoading ? (
                      <p className="text-sm text-muted">Loading deletion requests...</p>
                    ) : accountDeletion.reviewQueue.isError ? (
                      <p className="text-sm text-muted">Deletion requests could not be loaded.</p>
                    ) : (accountDeletion.reviewQueue.data ?? []).length === 0 ? (
                      <p className="text-sm text-muted">
                        No account deletion requests need review.
                      </p>
                    ) : (
                      (accountDeletion.reviewQueue.data ?? []).map((request) => (
                        <div
                          key={request.id}
                          className="border-brand-border/25 flex flex-wrap items-center justify-between gap-3 rounded-md border bg-card px-3 py-3"
                        >
                          <div>
                            <p className="font-medium text-foreground">
                              {request.requester_name || request.requester_email}
                            </p>
                            <p className="text-xs text-muted">
                              {request.requester_role} requested{' '}
                              {formatDateTime(request.requested_at)}
                            </p>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              onClick={() => void handleDeletionReview(request.id, 'REJECT')}
                              disabled={accountDeletion.reviewDeletion.isPending}
                            >
                              Decline
                            </Button>
                            <Button
                              type="button"
                              size="sm"
                              variant="destructive"
                              onClick={() => void handleDeletionReview(request.id, 'APPROVE')}
                              disabled={accountDeletion.reviewDeletion.isPending}
                            >
                              Approve
                            </Button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </section>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="bg-bg/40 px-5 py-3">
          <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AccountTab({
  active,
  children,
  icon,
  onClick,
}: {
  active: boolean;
  children: string;
  icon: WorklynxIconName;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-current={active ? 'page' : undefined}
      className={`inline-flex shrink-0 items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors ${active ? 'bg-card text-brand shadow-sm' : 'text-muted hover:bg-card hover:text-foreground'}`}
    >
      <WorklynxIcon name={icon} size={16} />
      {children}
    </button>
  );
}

function formatSessionDate(value: string | null) {
  if (!value) return 'unknown time';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'unknown time';

  return formatDateTime(date);
}

function getSignOutConfirmationTitle(scope: SignOutScope) {
  if (scope === 'local') return 'Sign out this device?';
  if (scope === 'others') return 'Sign out other devices?';
  return 'Sign out everywhere?';
}

function getSignOutConfirmationDescription(scope: SignOutScope) {
  if (scope === 'local') {
    return 'This browser or app session will end. Other signed-in devices will remain active.';
  }
  if (scope === 'others') {
    return 'All other browser and app sessions will end. This device will stay signed in.';
  }
  return 'Every WorkLynx session for this account will end, including this device.';
}

function canCancelDeletion(status: string) {
  return status === 'PENDING_REVIEW' || status === 'APPROVED' || status === 'FINALIZATION_FAILED';
}

function getDeletionRequestTitle(status: string) {
  if (status === 'PENDING_REVIEW') return 'Deletion request awaiting review';
  if (status === 'APPROVED') return 'Deletion request approved';
  if (status === 'FINALIZATION_FAILED') return 'Deletion finalisation needs attention';
  if (status === 'REJECTED') return 'Deletion request declined';
  return 'Deletion request closed';
}

function getDeletionRequestDescription(request: {
  status: string;
  retention_ends_at: string | null;
  review_note: string | null;
  finalization_error: string | null;
}) {
  if (request.status === 'PENDING_REVIEW') {
    return 'Your organisation owner or a platform admin must review this request before the retention period starts.';
  }
  if (request.status === 'APPROVED') {
    return `Your account will be finalised after ${formatSessionDate(request.retention_ends_at)} unless you cancel this request first.`;
  }
  if (request.status === 'FINALIZATION_FAILED') {
    return (
      request.finalization_error || 'Contact WorkLynx support to complete the deletion safely.'
    );
  }
  if (request.status === 'REJECTED') {
    return (
      request.review_note ||
      'Contact your organisation owner or WorkLynx support if you need more information.'
    );
  }
  return 'This request is no longer active.';
}
